/* ===== Dues: organization settings ===== */

import { useEffect, useState } from 'react'
import type { TenantContext } from '@zetyar/data-contracts'
import type { OrgDuesSettings } from '@zetyar/domain'
import { Building2 } from 'lucide-react'
import { Button } from '@/presentation/components/ui/Button'
import { Input } from '@/presentation/components/ui/Input'
import { Modal } from '@/presentation/components/ui/Modal'
import { webDuesRepository } from '@/infrastructure/repositories/dues'
import { FIELD_CLASS, TEXTAREA_CLASS, duesErrorMessage } from './duesFormat'

interface DuesSettingsModalProps {
  readonly isOpen: boolean
  readonly onClose: () => void
  readonly context: TenantContext
  readonly settings: OrgDuesSettings | null
  readonly onSaved: (settings: OrgDuesSettings) => void
}

interface FormState {
  monthlyAmount: string
  currency: string
  dueDay: string
  bankName: string
  accountType: string
  accountNumber: string
  accountHolder: string
  accountRut: string
  accountEmail: string
  instructions: string
  onlinePaymentEnabled: boolean
}

type TextField = Exclude<keyof FormState, 'onlinePaymentEnabled'>

function toForm(settings: OrgDuesSettings | null): FormState {
  return {
    monthlyAmount: settings ? String(settings.monthlyAmount) : '',
    currency: settings?.currency ?? 'CLP',
    dueDay: settings ? String(settings.dueDay) : '10',
    bankName: settings?.bankName ?? '',
    accountType: settings?.accountType ?? '',
    accountNumber: settings?.accountNumber ?? '',
    accountHolder: settings?.accountHolder ?? '',
    accountRut: settings?.accountRut ?? '',
    accountEmail: settings?.accountEmail ?? '',
    instructions: settings?.instructions ?? '',
    onlinePaymentEnabled: settings?.onlinePaymentEnabled ?? false,
  }
}

export function DuesSettingsModal({ isOpen, onClose, context, settings, onSaved }: DuesSettingsModalProps) {
  const [form, setForm] = useState<FormState>(() => toForm(settings))
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (isOpen) {
      setForm(toForm(settings))
      setError(null)
    }
  }, [isOpen, settings])

  const bind = (field: TextField) => ({
    value: form[field],
    onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
      setForm((current) => ({ ...current, [field]: event.target.value })),
  })

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    const saveSettings = webDuesRepository.saveSettings
    if (!saveSettings || isSaving) return

    const monthlyAmount = Number(form.monthlyAmount)
    const dueDay = Number(form.dueDay)
    if (!Number.isInteger(monthlyAmount) || monthlyAmount <= 0) {
      setError('Ingresa un monto mensual entero mayor a cero.')
      return
    }
    if (!Number.isInteger(dueDay) || dueDay < 1 || dueDay > 28) {
      setError('El día de vencimiento debe estar entre 1 y 28.')
      return
    }

    setIsSaving(true)
    setError(null)
    try {
      const saved = await saveSettings(context, {
        monthlyAmount,
        currency: form.currency,
        dueDay,
        bankName: form.bankName,
        accountType: form.accountType,
        accountNumber: form.accountNumber,
        accountHolder: form.accountHolder,
        accountRut: form.accountRut,
        accountEmail: form.accountEmail,
        instructions: form.instructions,
        onlinePaymentEnabled: form.onlinePaymentEnabled,
      })
      onSaved(saved)
    } catch (caught) {
      console.error('Error al guardar la configuración de cuotas:', caught)
      setError(duesErrorMessage(caught, 'No se pudo guardar la configuración de cuotas.'))
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        if (!isSaving) onClose()
      }}
      title="Configuración de cuotas"
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancelar
          </Button>
          <Button type="submit" form="dues-settings-form" isLoading={isSaving}>
            Guardar configuración
          </Button>
        </>
      }
    >
      <form id="dues-settings-form" className="space-y-5" onSubmit={submit}>
        <div className="grid gap-4 sm:grid-cols-3">
          <Input label="Monto mensual" type="number" min={1} step={1} required {...bind('monthlyAmount')} />
          <label className="flex flex-col gap-1.5 text-sm font-medium text-neutral-700 dark:text-neutral-200">
            Moneda
            <select className={FIELD_CLASS} {...bind('currency')}>
              <option value="CLP">CLP</option>
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
            </select>
          </label>
          <Input label="Día de vencimiento (1 a 28)" type="number" min={1} max={28} required {...bind('dueDay')} />
        </div>

        <fieldset className="space-y-3 border-t border-neutral-100 pt-4 dark:border-neutral-800">
          <legend className="flex items-center gap-2 text-sm font-bold text-neutral-800 dark:text-neutral-200">
            <Building2 className="h-4 w-4 text-primary-600" /> Datos para transferencias
          </legend>
          <p className="text-xs text-neutral-500 dark:text-neutral-400">
            Los socios activos verán estos datos en su sección de Cuotas. Deja en blanco lo que no corresponda.
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <Input label="Banco" maxLength={120} {...bind('bankName')} />
            <Input label="Tipo de cuenta" maxLength={60} {...bind('accountType')} />
            <Input label="Número de cuenta" maxLength={60} {...bind('accountNumber')} />
            <Input label="Titular" maxLength={160} {...bind('accountHolder')} />
            <Input label="RUT del titular" maxLength={20} {...bind('accountRut')} />
            <Input label="Correo para comprobantes" type="email" maxLength={254} {...bind('accountEmail')} />
          </div>
        </fieldset>

        <label className="flex flex-col gap-1.5 text-sm font-medium text-neutral-700 dark:text-neutral-200">
          Instrucciones para los socios (opcional)
          <textarea className={TEXTAREA_CLASS} maxLength={1000} rows={3} {...bind('instructions')} />
        </label>

        <label className="flex items-start gap-3 rounded-[13px] border border-neutral-200 p-3 dark:border-neutral-800">
          <input
            type="checkbox"
            className="mt-1 h-4 w-4 accent-primary-600"
            checked={form.onlinePaymentEnabled}
            onChange={(event) => setForm((current) => ({ ...current, onlinePaymentEnabled: event.target.checked }))}
          />
          <span>
            <span className="block text-sm font-semibold text-neutral-900 dark:text-neutral-100">Permitir pago en línea con Mercado Pago</span>
            <span className="block text-xs text-neutral-500 dark:text-neutral-400">
              Requiere la cuenta de Mercado Pago conectada en Configuración. Los pagos se confirman automáticamente cuando
              Mercado Pago los aprueba.
            </span>
          </span>
        </label>

        {error && <p role="alert" className="text-sm text-danger-600 dark:text-danger-300">{error}</p>}
      </form>
    </Modal>
  )
}
